import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { getTopic, type Topic } from "@/lib/topics";
import { getTopicDe } from "@/lib/topics.de";

/**
 * Topic pages in every supported language, resolved on the server.
 *
 * This is the piece that makes multilingual indexing possible without touching
 * the rendering path. A route loader calls this during SSR, TanStack serialises
 * the result into the page payload, and the client hydrates from that same
 * payload — so server and client render identical strings and there is no
 * hydration mismatch. That mismatch is what makes the obvious approach
 * (switching i18n language on the server) unsafe here: there is no client entry
 * in which to set the language before hydrateRoot, so React would discard the
 * server HTML.
 *
 * German is hand-written (topics.de.ts). The other languages go through the
 * existing cached translator: the first request for a given topic and language
 * pays for the translation, everything after reads the cache.
 */

/** Languages served by the generic localized route. English lives at /topics
 *  and German at /de/themen — both have their own hand-authored source. */
export const TOPIC_LOCALES = ["es", "fr", "it", "ru", "zh"] as const;
export type TopicLocale = (typeof TOPIC_LOCALES)[number];

/** Path segment per language, so the URL reads naturally in that language. */
export const TOPIC_PATH: Record<string, string> = {
  en: "topics",
  de: "themen",
  es: "temas",
  fr: "themes",
  it: "temi",
  ru: "temy",
  zh: "zhuti",
};

/** Every language version of one topic, for hreflang. */
export function topicAlternates(slug: string, site: string): { lang: string; href: string }[] {
  return [
    { lang: "en", href: `${site}/topics/${slug}` },
    { lang: "de", href: `${site}/de/themen/${slug}` },
    ...TOPIC_LOCALES.map((l) => ({ lang: l, href: `${site}/${l}/${TOPIC_PATH[l]}/${slug}` })),
  ];
}

/** Flatten a topic into an ordered string list, and back. Keeping the order in
 *  one place means the two halves cannot drift apart. */
function flatten(t: Topic): string[] {
  return [
    t.name,
    t.metaTitle,
    t.metaDescription,
    t.heroLead,
    ...t.thesis,
    ...t.inPractice.flatMap((s) => [s.title, s.body]),
    ...t.reality.works,
    ...t.reality.doesNot,
    ...t.faqs.flatMap((f) => [f.q, f.a]),
  ];
}

function rebuild(t: Topic, out: string[]): Topic {
  let i = 0;
  return {
    ...t,
    name: out[i++],
    metaTitle: out[i++],
    metaDescription: out[i++],
    heroLead: out[i++],
    thesis: t.thesis.map(() => out[i++]),
    inPractice: t.inPractice.map(() => ({ title: out[i++], body: out[i++] })),
    reality: {
      works: t.reality.works.map(() => out[i++]),
      doesNot: t.reality.doesNot.map(() => out[i++]),
    },
    // Search queries stay in the target language only if a human wrote them.
    // Machine-translating keyword lists produces phrases nobody types, so the
    // English ones are dropped rather than mistranslated.
    queries: [],
    faqs: t.faqs.map(() => ({ q: out[i++], a: out[i++] })),
  };
}

/** Section headings, translated alongside the content so the page does not mix
 *  languages. Ordered — see CHROME_KEYS in the route. */
const CHROME = [
  "What this looks like in practice",
  "What is true today",
  "This section exists because most pages in this category do not have one. Both columns are maintained against the product's own status list, not written by marketing.",
  "Works today",
  "Does not, and we will not pretend",
  "See the full status of every feature",
  "Check it against your own website",
  "The free Twin-Check reads your site the way an AI assistant would and tells you what it can and cannot answer about you. No account needed.",
  "Questions people actually ask",
  "More on how this works",
  "Read this page in English",
  "Check your AI-readability — free",
  "Build your Twin",
];

export type LocalizedTopic = { topic: Topic; chrome: string[] };

export const getLocalizedTopic = createServerFn({ method: "GET" })
  .inputValidator((d) =>
    z.object({ lang: z.string().min(2).max(5), slug: z.string().min(1).max(80) }).parse(d),
  )
  .handler(async ({ data }): Promise<LocalizedTopic | null> => {
    const base = data.lang.split("-")[0];
    const source = getTopic(data.slug);
    if (!source) return null;

    // Hand-written German beats any translation of it.
    if (base === "de") {
      const de = getTopicDe(data.slug);
      if (de) return { topic: de, chrome: CHROME };
    }
    if (base === "en") return { topic: source, chrome: CHROME };
    if (!(TOPIC_LOCALES as readonly string[]).includes(base)) return null;

    try {
      const { translateBatchCached } = await import("@/lib/translate-cache.server");
      const payload = [...flatten(source), ...CHROME];
      const out = await translateBatchCached(payload, base);
      // The translator returns the input unchanged on any failure, so a partial
      // result still renders — as English, which is better than a blank page.
      const contentLen = payload.length - CHROME.length;
      return {
        topic: rebuild(source, out.slice(0, contentLen)),
        chrome: out.slice(contentLen),
      };
    } catch {
      return { topic: source, chrome: CHROME };
    }
  });
